import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { ReactNode } from "react";
import { HoldCode } from "./types";
import { ORDER_STATUS_SHIPPED_TO_YOU_TEXT } from "./i18n-strings";
import { BadgeProps, Text, Link, Callout } from "@wootingkb/component-library";
import { ReplaceableKey } from "../../lib/helper/stringReplacer";
import RegionawareLink from "../../components/Common/RegionawareLink";

export enum PurchaseOrderStatus {
  New = "New",
  Placed = "Placed",
  EnRoute = "EnRoute",
  Delivered = "Delivered",
  PartiallyDelivered = "PartiallyDelivered",
  Planned = "Planned",
}

type OrderStatusMap = {
  [key in OrderStatus]: {
    tag: MessageDescriptor;
    description: OrderStatusDescriptionMapDefault;
    showContactButton?: boolean;
    color: string;
    variant: BadgeProps["variant"];
    deliveryProgress: DeliveryProgress;
  };
} & {
  [OrderStatus.OnHold]: {
    tag: MessageDescriptor;
    description: HoldOrderStatusDescriptionMap;
    showContactButton?: boolean;
    color: string;
    variant: BadgeProps["variant"];
    deliveryProgress: DeliveryProgress;
  };
};

// prelimenary and best solution i could come up with on the spot
type OrderStatusDescriptionMapDefault = {
  default: ReactNode;
};

type HoldOrderStatusDescriptionMap = OrderStatusDescriptionMapDefault & {
  [key in HoldCode]: ((custom_text?: string) => ReactNode) | ReactNode;
};

// Status reference: https://www.notion.so/wooting-technologies/Wooting-Hub-Improve-order-status-text-and-method-fa118aa0a96741e8b0ed84adaa66b96d
export enum OrderStatus {
  NewOrder = "NEW",
  Replacements = "RPI",
  Queue = "QUE", // - Queued for fulfilment
  Pending = "PND", // - Pending (pre-shipment, need to inspect or waiting for payment / verification)
  /**
   * @deprecated Use `OrderStatus.OnHold` instead.
   */
  CustomerActionRequired = "COM",
  OnHold = "HLD",
  BackOrder = "BCK",
  PreOrder = "PRE",
  ReadyToPack = "RDY",
  Packed = "PCK", // - Packed (waiting for pickup)
  Enroute = "ENR",
  Cancelled = "CNC",
  Delivered = "DLV",
  RMAReturn = "RMA",
  /**
   * @deprecated Use `OrderStatus.Pending` instead.
   */
  Upgrade = "UPG", // (pre-merge)
  Merged = "MRG",
  Special = "SPL", // (ignore)
  PaymentPending = "PMT",
  Authorized = "AUT",
}

enum DeliveryProgress {
  NotShipped,
  Shipped,
  /**
   * @deprecated use `DeliveryProgress.Shipped` instead.
   */
  Delivered,
}

const ESTIMATED_DELIVERY_ARTICLE_URL =
  "https://help.wooting.io/article/82-how-accurate-is-my-estimated-delivery-date";
const SHIPPING_PROBLEMS_ARTICLE_URL =
  "https://help.wooting.io/article/83-possible-problems-during-shipping-with-solutions";

const ORDER_STATUS_TAG_MESSAGES: Record<OrderStatus, MessageDescriptor> = {
  [OrderStatus.NewOrder]: msg({
    id: "Order_status_order_received",
    message: "Order Received",
  }),
  [OrderStatus.PaymentPending]: msg({
    id: "Order_status_payment_pending",
    message: "Payment Pending",
  }),
  [OrderStatus.Authorized]: msg({
    id: "Order_status_payment_authorized",
    message: "Payment Authorized",
  }),
  [OrderStatus.Replacements]: msg({
    id: "Order_status_replacement_parts_order",
    message: "Replacement Parts Order",
  }),
  [OrderStatus.Queue]: msg({
    id: "Order_status_queued_for_fulfillment",
    message: "Queued for Fulfillment",
  }),
  [OrderStatus.Pending]: msg({
    id: "Order_status_pending",
    message: "Pending",
  }),
  [OrderStatus.CustomerActionRequired]: msg({
    id: "Order_status_action_required",
    message: "Action Required",
  }),
  [OrderStatus.OnHold]: msg({
    id: "Order_status_on_hold",
    message: "On Hold",
  }),
  [OrderStatus.PreOrder]: msg({
    id: "Order_status_pre_order",
    message: "Pre-Order",
  }),
  [OrderStatus.BackOrder]: msg({
    id: "Order_status_back_order",
    message: "Back-Order",
  }),
  [OrderStatus.ReadyToPack]: msg({
    id: "Order_status_ready_to_pack",
    message: "Ready to Pack",
  }),
  [OrderStatus.Packed]: msg({
    id: "Order_status_pick_and_pack",
    message: "Pick & Pack",
  }),
  [OrderStatus.Enroute]: ORDER_STATUS_SHIPPED_TO_YOU_TEXT,
  [OrderStatus.Cancelled]: msg({
    id: "Order_status_cancelled",
    message: "Cancelled",
  }),
  [OrderStatus.Delivered]: ORDER_STATUS_SHIPPED_TO_YOU_TEXT,
  [OrderStatus.RMAReturn]: msg({
    id: "Order_status_return",
    message: "Return",
  }),
  [OrderStatus.Upgrade]: msg({
    id: "Order_status_upgrade",
    message: "Upgrade",
  }),
  [OrderStatus.Merged]: msg({
    id: "Order_status_merged",
    message: "Merged",
  }),
  [OrderStatus.Special]: msg({
    id: "Order_status_special",
    message: "Special",
  }),
};

function LearnMoreLink() {
  return (
    <Link
      href={ESTIMATED_DELIVERY_ARTICLE_URL}
      variant="underline"
      className="text-foreground-secondary"
      external
    >
      <Trans id="Order_status_common_learn_more">Learn more</Trans>
    </Link>
  );
}

const shippedToYouDescription = {
  default: (
    <>
      <Text>
        <Trans id="Order_status_shipped_on_its_way">
          Your order is on its way!
        </Trans>
      </Text>
      <Text>
        <Trans id="Order_status_shipped_track_via_number">
          You can now track your order through the provided tracking number.
        </Trans>
      </Text>
      <Text>
        <Trans id="Order_status_shipped_tracking_delay_note">
          Note: it typically takes 24-72 hours for a tracking link to resolve
          into a trackable shipment.
        </Trans>
      </Text>
      <Text>
        <Trans id="Order_status_shipped_problems_prefix">
          Shipment problem or question? Please see our{" "}
        </Trans>
        <Link
          href={SHIPPING_PROBLEMS_ARTICLE_URL}
          variant="underline"
          className="text-foreground-secondary"
          external
        >
          <Trans id="Order_status_shipped_problems_article">
            problems & solutions article
          </Trans>
        </Link>{" "}
        <Trans id="Order_status_shipped_problems_suffix">
          or you can contact support.
        </Trans>
      </Text>
    </>
  ),
};

const shippedToYouStatus: OrderStatusMap[OrderStatus.Enroute] = {
  tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.Enroute],
  description: shippedToYouDescription,
  color: "#4ac262",
  variant: "success",
  deliveryProgress: DeliveryProgress.Shipped,
};

export const orderStatusMap: OrderStatusMap = {
  NEW: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.NewOrder],
    description: {
      default: (
        <Text>
          <Trans id="Order_status_new_received_processing">
            Your order has been received and is being processed. Please check
            back later.
          </Trans>
        </Text>
      ),
    },
    color: "#4a8ec2",
    variant: "info",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  PMT: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.PaymentPending],
    description: {
      default: (
        <Text>
          <Trans id="Order_status_payment_pending_processing">
            Your payment is still being processed by our payment gateway. Your
            chosen payment method can take up to 72 hours to complete before we
            can proceed with your order. No action is required.
          </Trans>
        </Text>
      ),
    },
    color: "#4a8ec2",
    variant: "info",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  AUT: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.Authorized],
    description: {
      default: (
        <>
          <Text>
            <Trans id="Order_status_authorized_received">
              Your order has been received!
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_authorized_manual_review">
              Our automated system has flagged this purchase for additional
              manual review. If approved, you may be asked for additional
              verification details via email.
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_authorized_support_prefix">
              Questions? Reach out via our{" "}
            </Trans>
            <RegionawareLink
              href={`${ReplaceableKey.WootingStoreURL}/support`}
              external
              hideExternalIcon
            >
              <Trans id="Order_status_authorized_support_link">
                Support Wizard
              </Trans>
            </RegionawareLink>
            .
          </Text>
        </>
      ),
    },
    color: "#4a8ec2",
    variant: "info",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  RPI: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.Replacements],
    description: {
      default: (
        <>
          <Text>
            <Trans id="Order_status_replacements_received">
              Your replacement parts order has been received and is being
              processed. Please check back later.
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_replacements_batch_fulfillment">
              We make these replacement parts shipments periodically and in
              batches, please allow up to two weeks for your order to be
              fulfilled.
            </Trans>
          </Text>
        </>
      ),
    },
    color: "#4a8ec2",
    variant: "info",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  QUE: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.Queue],
    description: {
      default: (
        <>
          <Text>
            <Trans id="Order_status_queue_audit">
              Your order is in queue to be submitted for fulfillment. The queue
              is for a final internal audit on addresses, special requests, and
              other circumstances that can cause an issue with the shipment.
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_queue_delay">
              When a lot of orders are in queue, this can take a few days.
            </Trans>
          </Text>
          <LearnMoreLink />
        </>
      ),
    },
    showContactButton: true,
    color: "#4a8ec2",
    variant: "info",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  PND: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.Pending],
    description: {
      default: (
        <Text>
          <Trans id="Order_status_pending_admin_attention">
            Your order is on pause awaiting administrative attention from us. If
            your order has this status for longer than 24 hours, please contact
            us.
          </Trans>
        </Text>
      ),
    },
    showContactButton: true,
    color: "#4a8ec2",
    variant: "info",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  COM: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.CustomerActionRequired],
    description: {
      default: (
        <Text>
          <Trans id="Order_status_action_required_email">
            Check your email for details or reach out to us.
          </Trans>
        </Text>
      ),
    },
    showContactButton: true,
    color: "#4a8ec2",
    variant: "info",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  HLD: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.OnHold],
    description: {
      default: (
        <Text>
          <Trans id="Order_status_hold_default">
            This order is on hold and will not be shipped until either: a
            specific condition is met, or a specific action is taken. If you’re
            not sure what that condition/action is, please reach out to us.
          </Trans>
        </Text>
      ),
      [HoldCode.AddressUpdateRequired]: (hold_reason) => (
        <>
          <Text size="lg">
            <Trans id="Order_status_hold_address_check_title">
              Address Check
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_address_check_intro">
              Our system has detected that there may be an issue with your
              shipping address. The following reason was listed:
            </Trans>
          </Text>
          <Callout status="info" heading={hold_reason} />
          <Text>
            <Trans id="Order_status_hold_address_check_email">
              Please check your email for details, and update your address on
              the Hub as needed.
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_contact_us">
              Have questions or concerns? Contact Us
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.AddressUpdated]: (
        <>
          <Text size="lg">
            <Trans id="Order_status_hold_address_confirmation_title">
              Address Confirmation
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_address_confirmation_body">
              Our support team has changed your shipping address to help ensure
              your order delivers. Please confirm that these changes are
              accurate to release your order from hold.
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.RequestedCancel]: (
        <>
          <Text size="lg">
            <Trans id="Order_status_hold_cancellation_requested_title">
              Cancellation Requested
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_cancellation_requested_body">
              You’ve requested to cancel your order. Our team will process this
              request manually, and get back to you via email within 3 business
              days.
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_cancellation_requested_release">
              Change your mind? Stop your cancellation by releasing your order
              from Hold
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.ReviewNeeded]: (
        <>
          <Text size="lg">
            <Trans id="Order_status_hold_review_needed_title">
              Cancellation Requested
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_review_needed_body">
              We have received your confirmation to cancel this order. Your
              request will be reviewed by our team before processing, and you
              will hear from us via email within the next 5 business days.
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_review_needed_release">
              If you’d like to keep the order, use the button &quot;Release from
              hold&quot; below.
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.RefundNeeded]: (
        <>
          <Text size="lg">
            <Trans id="Order_status_hold_refund_in_progress_title">
              Refund in progress
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_refund_in_progress_body_1">
              We have accepted your cancellation request and will process your
              refund in the next 2-3 business days (exclude the weekend and
              holidays).
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_refund_in_progress_body_2">
              Once your refund has been issued, you will receive an email from
              us.
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.CancelPending]: (
        <>
          <Text size="lg">
            <Trans id="Order_status_hold_cancel_pending_title">
              Cancellation Requested
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_cancel_pending_change_mind">
              <strong>Change your mind?</strong> Stop your cancellation by
              clicking the Release from Hold button within 90 minutes.
            </Trans>
            <ol className="space-y-2 pl-6">
              <li>
                <s>
                  <Trans id="Order_status_hold_cancel_pending_confirmation">
                    <strong>Confirmation:</strong> This page confirms we’ve
                    received your cancellation request. ✅
                  </Trans>
                </s>
              </li>
              <li>
                <Trans id="Order_status_hold_cancel_pending_cancellation">
                  <strong>Cancellation:</strong> Once your order is canceled,
                  you’ll get an email notification.
                </Trans>
              </li>
              <li>
                <Trans id="Order_status_hold_cancel_pending_refund_process">
                  <strong>Refund process:</strong> Every refund needs to be
                  reviewed and processed manually by our team, it usually takes
                  3-5 business days.
                </Trans>
              </li>
              <li>
                <Trans id="Order_status_hold_cancel_pending_refund_completion">
                  <strong>Refund completion:</strong> Once your refund has been
                  issued, typically you&apos;ll receive your money back within
                  two weeks.
                </Trans>
              </li>
            </ol>
          </Text>
          <Text>
            <Trans id="Order_status_hold_cancel_pending_contact">
              Have questions or want a status update? Contact Us
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.PaymentVerification]: (
        <>
          <Text size="lg">
            <Trans id="Order_status_hold_payment_verification_title">
              Payment Verification
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_payment_verification_body">
              Our automated system has flagged this order for additional payment
              method verification. Please check your email for detailed
              instructions.
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_payment_verification_contact">
              Have questions or need support? Contact Us
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.OrderModification]: (
        <>
          <Text size="lg">
            <Trans id="Order_status_hold_order_modification_title">
              Order Modification in Progress
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_order_modification_body">
              This order is currently on hold while changes are being processed.
              Please check your email to see if we needed to send you an invoice
              for the changes, or if we’re waiting on you to confirm your
              preferences.
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_order_modification_contact">
              Have questions or need support? Contact Us
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.AwaitingPayment]: (
        <>
          <Text size="lg">
            <Trans id="Order_status_hold_awaiting_payment_title">
              Order Modification In Progress
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_awaiting_payment_body">
              This order is currently on hold while changes are being processed
              that require an invoice to be paid. Please check your email for
              the invoice. Once complete, respond to the email so that our team
              can finish the processing.
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_awaiting_payment_contact">
              Have questions or need support? Contact Us
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.ShipByDate]: (
        <>
          <Text>
            <Trans id="Order_status_hold_ship_by_date_body">
              This order was placed on hold to ensure it ships after a specific
              date.
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_ship_by_date_ready">
              Ready to receive your order?
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_ship_by_date_release">
              Release it back into queue by clicking the ’Release order’ button
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.NoShipping]: (
        <>
          <Text>
            <Trans id="Order_status_hold_no_shipping_body">
              This order cannot be shipped to the provided address. Please check
              your email for additional details.
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_no_shipping_contact">
              Haven’t received an email or need support? Contact Us
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.PendingDispute]: (
        <>
          <Text>
            <Trans id="Order_status_hold_pending_dispute_body">
              We have received notification of a charge dispute levied for this
              order. This order must stay on hold until the dispute is resolved.
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_pending_dispute_contact">
              Please reach out for additional assistance. Contact Us
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.CustomerHold]: (hold_reason) => (
        <>
          <Text>
            <Trans id="Order_status_hold_customer_hold_intro">
              You have placed this order on hold to ensure it doesn’t ship. The
              reason provided was:
            </Trans>
          </Text>
          <Callout status="info" heading={hold_reason} />
          <Text className="mt-4">
            <Trans id="Order_status_hold_customer_hold_ready">
              Ready to receive your order?
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_customer_hold_release">
              Release it back into queue by clicking the ’Release order’ button
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.WootingHold]: (
        <>
          <Text>
            <Trans id="Order_status_hold_wooting_hold_body">
              This order has been placed on hold.
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_wooting_hold_contact">
              Not sure why, want a status update, or need support? Contact Us
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.Waiting]: (condition) => (
        <>
          <Text>
            <Trans id="Order_status_hold_waiting_intro">
              This order is on hold until the following condition is met:
            </Trans>
          </Text>
          <Callout status="info" heading={condition} />
          <Text>
            <Trans id="Order_status_hold_waiting_contact">
              Has that condition been satisfied, want a status update, or need
              support? Contact Us.
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.PendingMerge]: (
        <>
          <Text size="lg">
            <Trans id="Order_status_hold_pending_merge_title">
              Pending Merge
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_pending_merge_body">
              This order is waiting to get merged into a different preorder. Our
              team will process this as quickly as possible!
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_pending_merge_contact">
              Have questions or want a status update? Contact Us
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.Owlab]: (
        <>
          <Text size="lg">
            <Trans id="Order_status_hold_owlab_title">
              Owlab Sweepstakes Entry
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_owlab_body">
              This order is waiting for the Owlab Sweepstakes to be completed,
              reviewed, and processed before the order can be shipped.
            </Trans>
          </Text>
        </>
      ),
      [HoldCode.V2ResellerPending]: (
        <>
          <Text size="lg">
            <Trans id="Order_status_hold_v2_reseller_title">
              Commercial Order
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_hold_v2_reseller_body">
              This order has been identified as being for commercial purposes.
              In order to ensure fairness for our customers purchasing products
              for the holiday season, this order won&apos;t get fulfilled until:
              29th of December, 2025
            </Trans>
          </Text>
        </>
      ),
    },
    showContactButton: true,
    color: "#4a8ec2",
    variant: "info",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  PRE: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.PreOrder],
    description: {
      default: (
        <Text>
          <Trans id="Order_status_preorder_body">
            When this order was placed not all items in the order were at the
            warehouse yet. You can track the status of the item arriving last
            below.
          </Trans>
        </Text>
      ),
    },
    color: "#c7c7c7",
    variant: "default",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  BCK: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.BackOrder],
    description: {
      default: (
        <Text>
          <Trans id="Order_status_backorder_body">
            When this order was placed, not all items in the order were in-stock
            at the warehouse. You can track the status of the back-/pre-ordered
            items below.
          </Trans>
        </Text>
      ),
    },
    color: "#c7c7c7",
    variant: "default",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  RDY: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.ReadyToPack],
    description: {
      default: (
        <>
          <Text>
            <Trans id="Order_status_ready_to_pack_submitted">
              Your order was submitted for fulfillment.
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_ready_to_pack_delay">
              When a lot of shipments are being processed it can take a few days
              before your order ships out.
            </Trans>
          </Text>
          <LearnMoreLink />
        </>
      ),
    },
    color: "#4ac262",
    variant: "success",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  PCK: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.Packed],
    description: {
      default: (
        <Text>
          <Trans id="Order_status_packed_body">
            Your order is at fulfillment for pick, pack, and ship. Once
            complete, the parcel is presented to the carrier and you will
            receive a tracking number.
          </Trans>
        </Text>
      ),
    },
    color: "#4ac262",
    variant: "success",
    deliveryProgress: DeliveryProgress.Shipped,
  },
  ENR: shippedToYouStatus,
  CNC: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.Cancelled],
    description: {
      default: (
        <>
          <Text>
            <Trans id="Order_status_cancelled_refunded">
              This order has been canceled and refunded
            </Trans>
          </Text>
          <Text>
            <Trans id="Order_status_cancelled_refund_delay">
              Note: It may take some time for the refund to appear in your
              account.
            </Trans>
          </Text>
        </>
      ),
    },
    color: "#d63c51",
    variant: "error",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  DLV: {
    ...shippedToYouStatus,
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.Delivered],
  },
  RMA: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.RMAReturn],
    description: {
      default: (
        <Text>
          <Trans id="Order_status_rma_body">
            This order failed to deliver or was returned for
            repair/replacement/refund.
          </Trans>
        </Text>
      ),
    },
    color: "#d6c13a",
    variant: "warning",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  UPG: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.Upgrade],
    description: { default: <></> },
    color: "#4ac262",
    variant: "success",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  MRG: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.Merged],
    description: { default: <></> },
    color: "#d6c13a",
    variant: "warning",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
  SPL: {
    tag: ORDER_STATUS_TAG_MESSAGES[OrderStatus.Special],
    description: { default: <></> },
    color: "#FFD700",
    variant: "warning",
    deliveryProgress: DeliveryProgress.NotShipped,
  },
};
